/**
 * Vehicle Selector - Form component
 */
import { GMXDropdown } from '../dropdown/dropdown'
import { sanitizeInput } from '../../../utils/sanitize'

interface VehicleSelectorModel {
	code: string,
	title: string,
	defaultImg: string,
}

export class GMXVehicleSelectorFormElement extends HTMLElement {
	yearDropdownEl: GMXDropdown
	modelDropdownEl: GMXDropdown
	vehicleImgEl: HTMLImageElement
	errorMsgEl: HTMLElement
	private originalVehicleImgSrc: string

	static observedAttributes = [
		'data-default-year',
		'data-default-model',
	]

	private boundYearDropdownChangeHandler = this._handleYearDropdownChange.bind(this)
	private boundModelDropdownChangeHandler = this._handleModelDropdownChange.bind(this)

	get vehicleSelectorServiceBaseUrl(): string {
		return this.getAttribute('data-vehicle-selector-service-base-url')
	}
	set vehicleSelectorServiceBaseUrl(val: string) {
		this.setAttribute('data-vehicle-selector-service-base-url', val)
	}

	get defaultYear(): string {
		return this.getAttribute('data-default-year')
	}
	set defaultYear(val: string) {
		this.setAttribute('data-default-year', val)
	}

	get defaultModel(): string {
		return this.getAttribute('data-default-model')
	}
	set defaultModel(val: string) {
		this.setAttribute('data-default-model', val)
	}

	async connectedCallback(): Promise<void> {
		this.yearDropdownEl = this.querySelector('gmx-dropdown[data-dropdown-name="vehicleYear"]') as GMXDropdown
		this.modelDropdownEl = this.querySelector('gmx-dropdown[data-dropdown-name="vehicleModel"]') as GMXDropdown
		this.vehicleImgEl = this.querySelector('img.vehicle-placeholder-img') as HTMLImageElement
		this.errorMsgEl = this.querySelector('.vs-error-msg')
		this.originalVehicleImgSrc = this.vehicleImgEl?.getAttribute('src') || ''

		await this._populateYearDropdown()

		this.yearDropdownEl.addEventListener('change', this.boundYearDropdownChangeHandler)
		this.modelDropdownEl.addEventListener('change', this.boundModelDropdownChangeHandler)

		if (this.defaultModel) {
			const selectedModel = this.modelDropdownEl.querySelector(`option[value="${this.defaultModel}"]`) as HTMLOptionElement

			if (selectedModel.dataset.defaultImg) {
				this.vehicleImgEl.src = `${selectedModel.dataset.defaultImg}&transparentBackgroundPng=true`
			}
		}
	}

	disconnectedCallback(): void {
		this.yearDropdownEl.removeEventListener('change', this.boundYearDropdownChangeHandler)
		this.modelDropdownEl.removeEventListener('change', this.boundModelDropdownChangeHandler)
	}

	/**
	 * Reset the vehicle selector to its initial empty state: no year/model selection, disabled model
	 * dropdown, placeholder image, no error/invalid state, and no default year/model attributes.
	 */
	reset(): void {
		this.removeAttribute('data-default-year')
		this.removeAttribute('data-default-model')

		const yearDropdownSelect = this.yearDropdownEl?.querySelector('select') as HTMLSelectElement | null
		if (yearDropdownSelect) {
			Array.from(yearDropdownSelect.options).forEach(option => {
				option.selected = option.value === ''
			})
			yearDropdownSelect.value = ''
			yearDropdownSelect.classList.remove('filled', 'invalid')
		}

		const modelDropdownSelect = this.modelDropdownEl?.querySelector('select') as HTMLSelectElement | null
		if (modelDropdownSelect) {
			modelDropdownSelect.innerHTML = '<option hidden="" value="" selected></option>'
			modelDropdownSelect.value = ''
			modelDropdownSelect.setAttribute('disabled', '')
			modelDropdownSelect.classList.remove('filled', 'invalid')
		}

		this.yearDropdownEl?.classList.remove('invalid')
		this.modelDropdownEl?.classList.add('disabled')
		this.modelDropdownEl?.classList.remove('invalid')

		if (this.vehicleImgEl && this.originalVehicleImgSrc) {
			this.vehicleImgEl.setAttribute('src', this.originalVehicleImgSrc)
		}

		this.errorMsgEl?.setAttribute('hidden', '')
	}

	/**
	 * Select the default year and model when those attributes are set. Used when a form panel
	 * reopens after reset so the vehicle selector returns to its configured defaults.
	 */
	applyDefaults(): void {
		if (!this.defaultYear || !this.yearDropdownEl) {
			return
		}

		const yearDropdownSelect = this.yearDropdownEl.querySelector('select') as HTMLSelectElement | null
		if (!yearDropdownSelect || yearDropdownSelect.value) {
			return
		}

		const defaultYearOption = yearDropdownSelect.querySelector(`option[value="${this.defaultYear}"]`)
		if (defaultYearOption) {
			yearDropdownSelect.value = this.defaultYear
			this._populateModelDropdown(this.defaultYear)
		} else {
			this._populateYearDropdown()
		}
	}

	attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
		/**
		 * Only want to update the default year and model IF
		 * 1) the component is already connected to the DOM, and
		 * 2) the new value is different from the old value, and
		 * 3) the user has not already made a selection (i.e. the dropdown value is still blank).  We don't want to override the user's selection with a new default if they have already made a selection, but we do want to populate the dropdowns with the new default options if they haven't made a selection yet.
		 */
		if (this.isConnected && newValue) {
			if (name === 'data-default-year' && oldValue !== newValue && this.yearDropdownEl && !this.yearDropdownEl?.querySelector('select')?.value) {
				this._populateYearDropdown()
			} else if (name === 'data-default-model' && oldValue !== newValue && this.modelDropdownEl && !this.modelDropdownEl?.querySelector('select')?.value) {
				const yearDropdownSelect = this.yearDropdownEl?.querySelector('select') as HTMLSelectElement | null
				const availableYear = this.defaultYear || yearDropdownSelect?.value

				if (availableYear) {
					this._populateModelDropdown(availableYear)
				}
			}
		}
	}

	/**
	 * Populate the year dropdown with data from the API.  If a default year is set, also populate the model dropdown for that year.
	 * @private
	 */
	private async _populateYearDropdown(): Promise<void> {
		const yearDropdownSelect = this.yearDropdownEl.querySelector('select') as HTMLSelectElement
		const years = await this._getDataFromApi('years')

		yearDropdownSelect.innerHTML = `
			<option hidden="" value="" ${!this.defaultYear ? 'selected' : ''}></option>
			${years.map(y => `<option value=${sanitizeInput(y.code)} ${this.defaultYear === y.code ? 'selected' : ''}>${sanitizeInput(y.title)}</option>`).join(' ')}
		`

		if (yearDropdownSelect.value) {
			await this._populateModelDropdown(yearDropdownSelect.value)
		}
	}

	/**
	 * Populate the model dropdown based on the selected year with data from the API.  If a default model is set, also set the vehicle image to the default image for that model.
	 * @param year
	 * @param resetSelection
	 * @private
	 */
	private async _populateModelDropdown(year: string, resetSelection = false): Promise<void> {
		const modelDropdownSelect = this.modelDropdownEl.querySelector('select') as HTMLSelectElement
		const models = await this._getDataFromApi(`models&year=${year}`)

		this.modelDropdownEl.classList.remove('disabled')
		modelDropdownSelect.removeAttribute('disabled')

		modelDropdownSelect.innerHTML = `<option hidden="" value="" ${!this.defaultModel ? 'selected' : ''}></option>`
		models.forEach(m => {
			const newOptionEl = document.createElement('option') as HTMLOptionElement
			newOptionEl.value = m.code
			newOptionEl.selected = this.defaultModel === m.code && !resetSelection
			newOptionEl.textContent = m.title
			newOptionEl.dataset.defaultImg = m.defaultImg

			modelDropdownSelect.appendChild(newOptionEl)

			if (this.defaultModel && !resetSelection) {
				const selectedModel = this.modelDropdownEl.querySelector(`option[value="${this.defaultModel}"]`) as HTMLOptionElement

				if (selectedModel?.dataset?.defaultImg) {
					this.vehicleImgEl.src = `${selectedModel.dataset.defaultImg}&transparentBackgroundPng=true`
				}
			}
		})
	}

	/**
	 * Helper method to retrieve data from the API based on the request type (years or models).  Returns an array of VehicleSelectorModel objects.
	 * @param requestType
	 * @private
	 */
	private async _getDataFromApi(requestType: string): Promise<VehicleSelectorModel[]> {
		try {
			const response = await fetch(`${this.vehicleSelectorServiceBaseUrl}?requestType=${requestType}`, {
				method: 'GET',
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
					"User-Agent": "insomnia/10.0.0",
					"clientApplicationId": "quantum",
				}
			})

			if (!response.ok) {
				this.errorMsgEl.removeAttribute('hidden')
				throw new Error(`Failed to fetch data: ${response.status}`)
			}

			this.errorMsgEl.setAttribute('hidden', '')
			const result = await response.json()
			return result?.options || []
		} catch (error) {
			console.error(`ERROR: Failed to retrieve data: ${error}`)
			return []
		}
	}

	/**
	 * Event handler for when the year dropdown value changes.  Populates the model dropdown based on the selected year.
	 * @param e
	 * @private
	 */
	private _handleYearDropdownChange(e: Event): void {
		const target = e.target as HTMLSelectElement

		if (target.value) {
			this._populateModelDropdown(target.value, true)
		}
	}

	/**
	 * Event handler for when the model dropdown value changes.  If the selected model has a default image, set the vehicle image to the default image for that model.
	 * @param e
	 * @private
	 */
	private _handleModelDropdownChange(e: Event): void {
		const target = e.target as HTMLSelectElement

		if (this.vehicleImgEl && target.value) {
			const selectedOption = target.selectedOptions[0]

			if (selectedOption && selectedOption.dataset.defaultImg) {
				this.vehicleImgEl.src = `${selectedOption.dataset.defaultImg}&transparentBackgroundPng=true`
			}
		}
	}
}

if (!customElements.get('gmx-vehicle-selector-form-element')) {
	customElements.define('gmx-vehicle-selector-form-element', GMXVehicleSelectorFormElement)
}
